'use client';
import { useEffect, useRef } from 'react';
import { getUserSpaces, switchUserSpace } from '@/app/apis';
import { useCredits, useFetch, useSubscription } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { buildTeamSpaces, findTeamSpaceById, isPersonalSpaceId, isTeamPlan } from '@/app/utils';
import { useRouter } from '@/i18n/routing';

/**
 * 企业版支付成功后自动切换到新创建的团队空间。
 *
 * 流程：
 * 1. 监听 `payStatus === 'success'` 且订单的 `plan_type` 是 `team_v2`，
 *    记录购买前的团队空间 id 集合，并主动刷新一次空间列表。
 * 2. 等支付结果弹窗关闭（`paymentPopupState` 由 true 转为 false）后，
 *    在最新 `teamSpaces` 中找出"购买前不存在"的非个人空间，调用 `switchUserSpace`
 *    切换并跳转到 `/home/recommend`，对齐 `TeamSpaceSwitcher` 的切换语义。
 *
 * 仅 my-subscription 页面会触发企业版购买，因此该副作用做成页面私有 hook，
 * 不下沉到 `usePayment`，避免污染通用支付流程。
 */
export const useTeamPurchaseAutoSwitch = () => {
  const router = useRouter();
  const { updateCredits } = useCredits();
  const { updateSubscription } = useSubscription();
  const pendingRef = useRef(false);
  const previousSpaceIdsRef = useRef<Set<string>>(new Set());

  const {
    payStatus,
    orderInfo,
    paymentPopupState,
    teamSpaces,
    updateUserInfo,
    updateTeamInfo,
    updateTeamSpaces,
    resetTaskFormStates,
    resetToolFormStates,
  } = useStore((state) => ({
    payStatus: state.payStatus,
    orderInfo: state.orderInfo,
    paymentPopupState: state.paymentPopupState,
    teamSpaces: state.teamSpaces,
    updateUserInfo: state.updateUserInfo,
    updateTeamInfo: state.updateTeamInfo,
    updateTeamSpaces: state.updateTeamSpaces,
    resetTaskFormStates: state.resetTaskFormStates,
    resetToolFormStates: state.resetToolFormStates,
  }));

  const { run: fetchUserSpaces } = useFetch(getUserSpaces, {
    manual: true,
    onSuccess: ({ data }) => {
      updateTeamSpaces(buildTeamSpaces(data?.spaces));
    },
  });

  const { run: switchSpace } = useFetch(switchUserSpace, {
    manual: true,
    onSuccess: ({ data }) => {
      if (!data?.user) {
        pendingRef.current = false;
        return;
      }
      updateUserInfo(data.user);
      const nextSpaceId = data.user.internal?.space_id;
      if (isPersonalSpaceId(nextSpaceId)) {
        updateTeamInfo(null);
      } else {
        updateTeamInfo(findTeamSpaceById(teamSpaces, nextSpaceId));
      }
      resetTaskFormStates();
      resetToolFormStates();
      updateCredits();
      updateSubscription();
      pendingRef.current = false;
      router.push('/home/recommend');
    },
  });

  // 1) 成功 + 企业版：快照购买前的非个人空间集合，并主动刷新空间列表
  useEffect(() => {
    if (payStatus !== 'success') return;
    if (!isTeamPlan(orderInfo?.plan_type)) return;
    if (pendingRef.current) return;

    pendingRef.current = true;
    previousSpaceIdsRef.current = new Set(
      teamSpaces.map((s) => s.space_id).filter((id): id is string => Boolean(id) && !isPersonalSpaceId(id)),
    );
    fetchUserSpaces();
  }, [payStatus, orderInfo?.plan_type]);

  // 2) 弹窗关闭 + 最新 spaces 已就绪：找新增空间并切换
  useEffect(() => {
    if (!pendingRef.current) return;
    if (paymentPopupState) return;

    const newSpace = teamSpaces.find(
      (space) =>
        !!space.space_id && !isPersonalSpaceId(space.space_id) && !previousSpaceIdsRef.current.has(space.space_id),
    );
    if (!newSpace?.space_id) {
      // 兜底：空间列表尚未刷新出新条目，重新拉一次，依赖下次 effect 触发
      fetchUserSpaces();
      return;
    }
    switchSpace(newSpace.space_id);
  }, [paymentPopupState, teamSpaces]);
};
